// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Save, Check, UserRound, Mic } from 'lucide-react';

import { FillPage } from '@/components/layout/PageContainer';
import { PageLoader } from '@/components/feedback/PageLoader';
import { AI_AGENT_FIELD_LIMITS, ROUTES, USER_ROLES } from '@/utilities/constant';
import { AGENT_TYPE_LABELS } from '@/utilities/aiAgentsStore';
import { getUser } from '@/utilities/setting';
import { saveBot, saveDraft, getDraft, getAgent } from '@/service/aiAgentApi';

import {
  buildDataVariablesGroups,
  normalizeVariableType,
  normalizeVariableSource,
} from '../builder-components/DataVariablesSection';

import { FlattenSectionsContext } from '../builder-components/CollapsibleSection';
import PersonaTypeSection from '../builder-components/PersonaTypeSection';
import VoiceIdSection from '../builder-components/VoiceIdSection';
import HeadingInfo from '../builder-components/HeadingInfo';

import BuilderTree from './BuilderTree';
import ImportJsonDialog from './ImportJsonDialog';
import { flatten, rollUp } from './treeState';
import { validateLaya } from './layaLlm';
import {
  hydrateAgent,
  buildSavePayload as buildSavePayloadFor,
  buildFlowPayload as buildFlowPayloadFor,
  missingForPublish,
  defaultFlowText,
} from './agentPayload';

import './ui/studio.css';
import { c, StudioContext } from './ui/ui';
import { useStudioData } from './ui/lookups';
import { readiness, heroInfo } from './ui/studioState';
import { Header, Hero, Rail, Dock, ViewBar } from './ui/chrome';
import NavSearch from './ui/NavSearch';
import FlowCard from './ui/FlowCard';
import FlowNotesSection from './ui/FlowNotesSection';
import LanguageSection from './ui/sections/LanguageSection';
import KnowledgeSection from './ui/sections/KnowledgeSection';
import InstructionsSection from './ui/sections/InstructionsSection';
import FunctionsSection from './ui/sections/FunctionsSection';
import McpSection from './ui/sections/McpSection';
import VariablesSection from './ui/sections/VariablesSection';
import LayaSection from './ui/sections/LayaSection';
import CompletionSection from './ui/sections/CompletionSection';
import FollowUpSection from './ui/sections/FollowUpSection';
import HandoverSection from './ui/sections/HandoverSection';


const NOTES = {
  languages: 'Skip it and the agent speaks English (India).',
  functions: 'Only needed if the agent must book or look things up through your API.',
  laya: 'Leave it off and every question goes to the AI.',
  handover: 'Off by default. Turn it on to let callers reach a live person.',
  variables: 'Only needed to capture values such as a name or phone number.',
  mcp: 'Only needed if the AI must use outside tools.',
};


function Detail({ id, title, help, icon: Icon, done, children }) {
  return (
    <section className={c('sec', done && 'ok')} id={`ags-s-${id}`} data-sec={id}>
      <div className={c('sh')}>
        <span className={c('ico')}>{done ? <Check className="ags-i" /> : <Icon className="ags-i" />}</span>
        <div>
          <h2>
            {!done && <span aria-hidden className={c('star')}>* </span>}
            {title}
          </h2>
          {help && <p>{help}</p>}
        </div>
        {done ? (
          <span className={c('st done')}>Done</span>
        ) : (
          <span className={c('reqpill')}>
            <b aria-hidden>!</b> Required
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

const GROUP_IDS = ['basics', 'knows', 'ending', 'advanced'];

const AgentBuilderEditor = ({ loadedAgent }: any) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const agentId = searchParams.get('id') || loadedAgent?.agent_id || '';
  const agentType = searchParams.get('type');
  const agentTypeLabel = AGENT_TYPE_LABELS[agentType] || 'Voice Agent';

  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

  const initialState = useRef(
    hydrateAgent(loadedAgent, {
      agentType,
      agentName: searchParams.get('name') || undefined,
      user,
      normalizers: { normalizeType: normalizeVariableType, normalizeSource: normalizeVariableSource },
    }),
  ).current;

  const [agentName, setAgentName] = useState(initialState.agentName);
  const [draftId, setDraftId] = useState(searchParams.get('draftId') || loadedAgent?.id || null);
  const draftIdRef = useRef(draftId);
  const [status, setStatus] = useState(loadedAgent?.status || 'draft');
  const [viewOnly, setViewOnly] = useState(isSuperAdmin || status === 'published');
  const [saving, setSaving] = useState(false);

  const [selectedKbId, setSelectedKbId] = useState(initialState.selectedKbId);
  const [instructions, setInstructions] = useState(initialState.instructions);
  const [functions, setFunctions] = useState(initialState.functions);
  const [followUp, setFollowUp] = useState(initialState.followUp);
  const [dataVariables, setDataVariables] = useState(initialState.dataVariables);
  const [voiceId, setVoiceId] = useState(initialState.voiceId);
  const [language, setLanguage] = useState(initialState.language);
  const [languageId, setLanguageId] = useState(initialState.languageId);
  const [personaType, setPersonaType] = useState(initialState.personaType);
  const [personaData, setPersonaData] = useState(initialState.personaData);
  const [conversationFlow, setConversationFlow] = useState(initialState.conversationFlow);
  const [mcpServers, setMcpServers] = useState(initialState.mcpServers);
  const [humanHandoverEnabled, setHumanHandoverEnabled] = useState(initialState.humanHandoverEnabled);
  const [completionRules, setCompletionRules] = useState(initialState.completionRules);
  // new jsonb columns — null for every agent saved before this feature
  const [laya, setLaya] = useState(initialState.laya);
  const [llm, setLlm] = useState(initialState.llm);
  const layaGloballyDisabled = loadedAgent?.laya_globally_enabled === false;

  const [flowGenerated, setFlowGenerated] = useState(Boolean(loadedAgent?.conversational_flow || loadedAgent?.flow_chart_data));
  const defaultFlow = (defaultFlowText({ instructions, agentName }) || '').trim().slice(0, AI_AGENT_FIELD_LIMITS.CONVERSATIONAL_FLOW_MAX);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(loadedAgent?.updated_at || new Date().toISOString());
  const lastUpdatedLabel = new Date(lastUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const [selectedId, setSelectedId] = useState('kb');
  const [expanded, setExpanded] = useState({ knows: true, basics: true, laya: true, ending: true });
  const searchRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [popOpen, setPopOpen] = useState(false);
 
  const [touched, setTouched] = useState(() =>
    loadedAgent ? { functions: true, languages: true, handover: true, variables: true, mcp: true, laya: true } : {},
  );
  const [attempted, setAttempted] = useState(false);
  const scrollRef = useRef(null);
  const colsRef = useRef(null);

  
  const touch = (key) =>
    setTouched((t) => {
      if (t[key]) return t;
      const next = { ...t, [key]: true };
      next[key.split('.')[0]] = true;
      return next;
    });

  
  const IMPORT_TOUCHED_KEYS = [
    'kb', 'instructions', 'functions', 'mcp', 'variables',
    'persona', 'persona.type', 'persona.name', 'persona.greeting', 'persona.role',
    'voice', 'languages', 'laya', 'handover', 'completion', 'followup', 'flow',
  ];
  const skipDoneToast = useRef(false);

  /** One JSON import fans out into every field; all of it stays editable. */
  const applyImport = ({ state: next, warnings, has = {} }) => {
    skipDoneToast.current = true;
    setTimeout(() => {
      skipDoneToast.current = false;
    }, 1500);
    setTouched((t) => ({ ...t, ...Object.fromEntries(IMPORT_TOUCHED_KEYS.map((k) => [k, true])) }));
    if (has.name !== false) setAgentName(next.agentName);
    setSelectedKbId(next.selectedKbId);
    setInstructions(next.instructions);
    setFunctions(next.functions);
    setFollowUp(next.followUp);
    setDataVariables(next.dataVariables);
    setVoiceId(next.voiceId);
    setLanguage(next.language || 'en-IN');
    setLanguageId(next.languageId);
    setPersonaType(next.personaType);
    setPersonaData(next.personaData);
    setConversationFlow(next.conversationFlow);
    setMcpServers(next.mcpServers);
    setHumanHandoverEnabled(next.humanHandoverEnabled);
    setCompletionRules(next.completionRules);
    setLaya(next.laya);
    setLlm(next.llm);
    setFlowGenerated(Boolean(next.conversationFlow));
    setViewOnly(false);
    toast.success('Form filled from JSON — check it over before publishing.');
    warnings.forEach((w) => toast.warning(w));
  };

  const readOnly = viewOnly;
  const canImport = !isSuperAdmin;
  const layaCheck = validateLaya(laya);

  // ------------------------------------------------- payloads (v1 contract)
  // One source of truth, tested in agentPayload.selfcheck.mjs.
  const data = useStudioData();
  const effLanguage = language || 'en-IN';
  
  const effLanguageId = data.languages.items.find((l) => l.code === effLanguage)?.id ?? languageId ?? null;

 
  useEffect(() => {
    const { items, loading } = data.languages;
    if (loading || !items.length || !language) return;
    const hit = items.find((l) => l.code === language);
    if (hit) {
      if (String(languageId) !== String(hit.id)) setLanguageId(hit.id);
      return;
    }
    const def = items.find((l) => l.code === 'en-IN');
    if (def) {
      setLanguage('en-IN');
      setLanguageId(def.id);
    }
  }, [data.languages.loading, data.languages.items, language, languageId]);
  const formState = {
    agentId,
    agentType,
    agentName,
    draftId: draftIdRef.current || draftId,
    user,
    selectedKbId,
    instructions,
    functions,
    followUp,
    dataVariables,
    voiceId,
    language: effLanguage,
    languageId: effLanguageId,
    personaType,
    personaData,
    conversationFlow,
    mcpServers,
    humanHandoverEnabled,
    completionRules,
    laya,
    llm,
    flowGenerated,
    defaultCompletions: data.defaultCompletions.items,
  };

  const buildFlowPayload = () => buildFlowPayloadFor(formState);
  const buildSavePayload = (opts = {}) =>
    buildSavePayloadFor(formState, { buildDataVariables: buildDataVariablesGroups, ...opts });

  const buildDraftPayload = () => ({ ...(draftIdRef.current ? { id: draftIdRef.current } : {}), ...buildSavePayload() });

  // ------------------------------------------------- autosave (unchanged)
  const autosaveTimer = useRef(null);
  const isFirstRender = useRef(true);
  const dirtyRef = useRef(false);
  const saveChainRef = useRef(Promise.resolve());
  const saveErrorShownRef = useRef(false);
  const latestPayloadRef = useRef(null);
  const [draftState, setDraftState] = useState('idle'); // idle | saving | saved | error

  const autosaveEnabled = !viewOnly && status !== 'published' && !!agentName?.trim();
  const autosaveEnabledRef = useRef(autosaveEnabled);
  autosaveEnabledRef.current = autosaveEnabled;
  latestPayloadRef.current = buildDraftPayload();

  const persistDraft = () => {
    saveChainRef.current = saveChainRef.current.then(async () => {
      if (!dirtyRef.current || !autosaveEnabledRef.current) return;
      dirtyRef.current = false;
      try {
        const currentId = draftIdRef.current;
        const saved = await saveDraft({
          ...latestPayloadRef.current,
          ...(currentId ? { id: currentId, draft_id: currentId } : {}),
        });
        if (!draftIdRef.current && saved?.id) {
          draftIdRef.current = saved.id;
          setDraftId(saved.id);
          const next = new URLSearchParams(window.location.search);
          next.set('draftId', String(saved.id));
          setSearchParams(next, { replace: true });
        }
        saveErrorShownRef.current = false;
        setLastUpdatedAt(new Date().toISOString());
        setDraftState('saved');
      } catch (err) {
        dirtyRef.current = true;
        setDraftState('error');
        console.error('Draft autosave failed', err);
        if (!saveErrorShownRef.current) {
          saveErrorShownRef.current = true;
          toast.error(typeof err === 'string' ? `Draft not saved: ${err}` : 'Draft could not be saved. Please try again.');
        }
      }
    });
    return saveChainRef.current;
  };

  useEffect(() => {
    if (!autosaveEnabled) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dirtyRef.current = true;
    setDraftState('saving');
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(persistDraft, 1000);
    return () => clearTimeout(autosaveTimer.current);
  }, [
    agentName,
    selectedKbId,
    instructions,
    functions,
    followUp,
    dataVariables,
    voiceId,
    language,
    languageId,
    personaType,
    personaData,
    conversationFlow,
    mcpServers,
    humanHandoverEnabled,
    completionRules,
    laya,
    llm,
  ]);

  useEffect(
    () => () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      if (dirtyRef.current) persistDraft();
    },
    [],
  );

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current && autosaveEnabledRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ------------------------------------------------- what is still missing
  const filledRules = completionRules.filter((r) => r.type?.trim() || r.description?.trim());
  const hasInstruction = instructions.some((i) => i.enabled !== false && i.body?.trim());
  const personaComplete =
    !!personaType &&
    !!personaData.greeting?.trim() &&
    !!personaData.display_name?.trim() &&
    !!personaData.role_description?.trim();



  const completionValid = filledRules.every(
    (r) => !!r.type?.trim() && /^[a-z0-9_]+$/.test(r.type.trim()) && !!r.description?.trim(),
  );
  const mcpComplete = mcpServers.some((s) => s.name?.trim() && s.url?.trim());
  const mcpHalf = mcpServers.some((s) => !!s.name?.trim() !== !!s.url?.trim());
  const followHalf = (followUp?.steps || []).some((s) => !!s.message?.trim() !== !!String(s.waitTime ?? '').trim());
    const followDone = !followHalf && (followUp?.steps || []).some((s) => s.message?.trim() && String(s.waitTime ?? '').trim());

  const missing = missingForPublish(formState);

  const seen = (id) => readOnly || !!touched[id];
  const st = (ok, busy) => (ok ? 'done' : busy ? 'attention' : 'empty');

  const nodes = useMemo(
    () => [
      {
        id: 'knows',
        label: 'What it knows and does',
        children: [
          { id: 'kb', need: 'required', label: 'Knowledge Base', status: st(!!selectedKbId) },
          { id: 'instructions', need: 'required', label: 'Instructions', badge: String(instructions.length), status: st(hasInstruction) },
          { id: 'functions', need: 'optional', label: 'Functions', badge: functions.length ? String(functions.length) : '', status: seen('functions') ? 'done' : 'empty' },
        ],
      },
      {
        id: 'basics',
        label: 'Basics',
        children: [
          { id: 'persona', need: 'required', label: 'Persona', status: personaComplete ? 'done' : personaType ? 'attention' : 'empty' },
          { id: 'voice', need: 'required', label: 'Voice', status: voiceId ? 'done' : 'empty' },
          { id: 'languages', need: 'optional', label: 'Languages', status: language && seen('languages') ? 'done' : 'empty' },
        ],
      },
      {
        id: 'laya',
        label: 'Fast answers',
        // optional while off; once switched on it must be filled in
        need: laya.enabled ? 'required' : 'optional',
        status: laya.enabled ? undefined : 'paused',
        children: [
          ...laya.answers.map((a, i) => ({
            id: `laya-answer-${a.id || i}`,
            label: a.description?.trim() || `Answer ${i + 1}`,
            status: a.enabled === false ? 'paused' : /[<>]/.test(a.answer || '') ? 'attention' : a.answer?.trim() ? 'done' : 'empty',
          })),
          ...(laya.enabled && !laya.answers.some((a) => a.description?.trim() || a.answer?.trim())
            ? [{ id: 'laya-empty', need: 'required', label: 'Add at least one answer', status: 'attention' }]
            : []),
        ],
      },
      {
        id: 'ending',
        label: 'How calls end',
        children: [
          { id: 'handover', need: 'optional', label: 'Human Handover', status: seen('handover') ? 'done' : humanHandoverEnabled ? 'empty' : 'paused' },
         { id: 'completion', need: 'optional', label: 'Completion Rules', status: st(completionValid, filledRules.length > 0) },
        { id: 'followup', need: 'required', label: 'Follow-up Rules', status: followHalf ? 'attention' : followDone ? 'done' : attempted || touched.followup ? 'attention' : 'empty' },
        ],
      },
      {
        id: 'advanced',
        label: 'Advanced',
        children: [
          { id: 'variables', need: 'optional', label: 'Data Variables', status: seen('variables') ? 'done' : 'empty' },
          {
            id: 'flow',
            need: 'required',
            label: 'Conversation Flow',

            status: flowGenerated ? 'done' : conversationFlow?.trim() ? (attempted ? 'attention' : 'empty') : 'empty',
            badge: !flowGenerated && conversationFlow?.trim() && !attempted ? 'Generate' : undefined,
          },
          { id: 'mcp', need: 'optional', label: 'MCP', status: mcpHalf ? 'attention' : seen('mcp') ? 'done' : 'empty' },
        ],
      },
    ],
    [
      personaComplete, personaType, voiceId, language, instructions, hasInstruction, selectedKbId, functions,
      laya, filledRules.length, completionValid, mcpComplete, mcpHalf, humanHandoverEnabled, followUp, dataVariables,
      conversationFlow, flowGenerated, mcpServers, touched, readOnly, attempted,
    ],
  );

  const summaryLines = [
    `Greets callers as ${personaData.display_name?.trim() || 'your agent'}${voiceId ? '' : ' (no voice picked yet)'}.`,
    hasInstruction ? `Follows ${instructions.filter((i) => i.enabled !== false).length} instruction(s)${selectedKbId ? ' and your knowledge base' : ''}.` : 'Has no instructions yet.',
    laya.enabled && laya.answers.length
      ? `Answers ${laya.answers.filter((a) => a.enabled !== false).length} common question(s) instantly; everything else goes to the AI.`
      : 'Every question goes to the AI — fast answers are off.',
    humanHandoverEnabled ? 'Can transfer the call to a person.' : 'Ends the call instead of transferring to a person.',
  ];

  // ------------------------------------------------- actions
  const handleBack = async () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    await persistDraft();
    navigate(`/${ROUTES.AI_AGENT.AGENTS}`);
  };

  const handlePublish = async () => {
    if (readOnly) {
      setViewOnly(false);
      return;
    }
    if (missing.length) {
      setAttempted(true);
      setPopOpen((o) => !o);
      return;
    }
    if (data.languages.loading) {
      toast.info('Languages are still loading. Please try again in a moment.');
      return;
    }
    setSaving(true);
    try {
      dirtyRef.current = false;
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      await saveChainRef.current;
      const result = await saveBot(buildSavePayload());
      const savedAgentId = result?.agent_id || result?.id;
       if (result?.local_sync_failed) {
      toast.warning('Agent published, but its editor copy could not be updated. Re-publish if the settings look old when you reopen it.');
      }
      if (!agentId && savedAgentId) {
        const next = new URLSearchParams(searchParams);
        next.set('id', savedAgentId);
        next.delete('draftId');
        setSearchParams(next, { replace: true });
      }
      setStatus('published');
      toast.success('Agent published');
      setLastUpdatedAt(new Date().toISOString());
      setViewOnly(true);
      navigate(`/${ROUTES.AI_AGENT.AGENTS}`);
    } catch (err) {
      dirtyRef.current = true;
      const message = typeof err === 'string' ? err : 'Failed to save agent';
      toast.error(
        message.includes('duplicate key') || message.includes('idx_agent_config_nm')
          ? 'An agent with this name already exists. Please choose a different name.'
          : message,
      );
    } finally {
      setSaving(false);
    }
  };


  const ctx = { touched, attempted, flowGenerated, readOnly };
  const items = readiness(formState, missing, ctx, data);
  const hero = heroInfo(formState, items, data);

  
  const nav = { next: () => null, onNext: () => {} };

  const renderDetail = () => {
    const id = selectedId.startsWith('laya-') ? 'laya' : selectedId;
    switch (id) {
      case 'basics':
      case 'persona':
        return (
          <FlattenSectionsContext.Provider value>
            <Detail id="persona" title="Persona" icon={UserRound} done={personaComplete} help="Who the caller thinks they are talking to, and the first thing they hear.">
              <PersonaTypeSection value={personaType} onChange={setPersonaType} personaData={personaData} onPersonaDataChange={setPersonaData} />
            </Detail>
          </FlattenSectionsContext.Provider>
        );
      case 'voice':
        return (
          <FlattenSectionsContext.Provider value>
            <Detail id="voice" title="Voice" icon={Mic} done={!!voiceId} help="Pick a voice so callers hear someone. Press play to hear it first.">
              <VoiceIdSection value={voiceId} onChange={setVoiceId} />
            </Detail>
          </FlattenSectionsContext.Provider>
        );
      case 'languages':
        return (
          <LanguageSection
            {...nav}
            value={language}
            onChange={({ id: lid, code }) => {
              setLanguageId(lid);
              setLanguage(code);
            }}
          />
        );
      case 'instructions':
        return <InstructionsSection {...nav} value={instructions} onChange={setInstructions} />;
      case 'kb':
        return <KnowledgeSection {...nav} value={selectedKbId} onChange={setSelectedKbId} />;
      case 'functions':
        return <FunctionsSection {...nav} value={functions} onChange={setFunctions} />;
      case 'laya':
        return <LayaSection {...nav} value={laya} onChange={setLaya} globallyDisabled={layaGloballyDisabled} />;
      case 'completion':
            return <CompletionSection {...nav} value={completionRules} onChange={setCompletionRules} />;
      case 'handover':
        return <HandoverSection {...nav} value={humanHandoverEnabled} onChange={setHumanHandoverEnabled} />;
      case 'followup':
        return <FollowUpSection {...nav} value={followUp} onChange={setFollowUp} />;
      case 'variables':
        return <VariablesSection {...nav} value={dataVariables} onChange={setDataVariables} />;
      case 'flow':
        return <FlowNotesSection value={conversationFlow} onChange={setConversationFlow} defaultValue={defaultFlow} />;
      case 'mcp':
        return <McpSection {...nav} value={mcpServers} onChange={setMcpServers} />;
      default:
        return null;
    }
  };

  // Previous / Next walk the tree in the order it is drawn, so the buttons and
  // the sidebar always agree on "what comes after this".
  const order = useMemo(() => flatten(nodes).filter((n) => !GROUP_IDS.includes(n.id) && !n.id.startsWith('laya-')), [nodes]);


  const DONE_TOAST_DELAY = 1500;
  const prevStatuses = useRef(null);
  const doneTimers = useRef({});
  const pendingDone = useRef(new Set());
  const latestStatuses = useRef({});
  useEffect(() => {
    const now = rollUp(nodes);
    latestStatuses.current = now;
    const prev = prevStatuses.current;
    prevStatuses.current = now;
    if (!prev || readOnly) return;
    if (skipDoneToast.current) {
      skipDoneToast.current = false;
      return;
    }

    const leaves = flatten(nodes).filter((n) => !n.children?.length && !n.id.startsWith('laya-'));

    leaves.forEach((n) => {
      if (prev[n.id] !== 'done' && now[n.id] === 'done') pendingDone.current.add(n.id);
      if (now[n.id] !== 'done' && pendingDone.current.has(n.id)) {
        pendingDone.current.delete(n.id);
        clearTimeout(doneTimers.current[n.id]);
      }
    });

    
    leaves.forEach((n) => {
      if (!pendingDone.current.has(n.id)) return;
      clearTimeout(doneTimers.current[n.id]);
      doneTimers.current[n.id] = setTimeout(() => {
        pendingDone.current.delete(n.id);
        if (latestStatuses.current[n.id] === 'done') toast.success(`${n.label} completed`, { id: `done-${n.id}` });
      }, DONE_TOAST_DELAY);
    });
  }, [nodes, readOnly, completionRules, mcpServers, followUp, laya, instructions, personaData]);
  useEffect(() => () => Object.values(doneTimers.current).forEach(clearTimeout), []);
  const orderIndex = order.findIndex((n) => n.id === (selectedId.startsWith('laya-') ? 'laya' : selectedId));
  const prevNode = orderIndex > 0 ? order[orderIndex - 1] : null;
  const nextNode = orderIndex >= 0 ? order[orderIndex + 1] : order[0];


  const visit = (id) => {
    if (!id) return;
    if (id.startsWith('laya-')) id = 'laya';
    touch(id);
    if (id === 'persona') ['persona.type', 'persona.name', 'persona.greeting', 'persona.role'].forEach(touch);
  };
  const lastSelected = useRef(selectedId);
  useEffect(() => {
    if (lastSelected.current !== selectedId) visit(lastSelected.current);
    lastSelected.current = selectedId;
  }, [selectedId]);

  const goTo = (node) => {
    if (!node) return;
    setSelectedId(node.id);
    const parent = nodes.find((n) => n.children?.some((c) => c.id === node.id));
    if (parent) setExpanded((e) => ({ ...e, [parent.id]: true }));
    const box = scrollRef.current;
    const cols = colsRef.current;
    if (box && cols && cols.getBoundingClientRect().top < box.getBoundingClientRect().top) {
      cols.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  const selectNode = (id) => {
    const node = order.find((n) => n.id === id);
    if (node) goTo(node);
  };

  
  const flowCardRef = useRef(null);

  const pulseFlowCard = () => {
    const card = document.getElementById('ags-flowcard');
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.classList.remove('ags-pulse');
    void card.offsetWidth;
    card.classList.add('ags-pulse');
  };


  const generateFlowFromFix = () => {
    setPopOpen(false);
    const card = flowCardRef.current;
    if (card?.canGenerate) {
      card.generate();
    } else {
      pulseFlowCard();
      toast.info('Attach a knowledge base and turn on an instruction, then generate the flow chart.');
    }
  };

  const jumpTo = (id) => {
    const target = id === 'basics' ? 'persona' : id;
    const node = order.find((n) => n.id === target);
    if (!node) return;
    setPopOpen(false);
    visit(target);

    if (!(target === 'flow' && !flowGenerated)) goTo(node);
    setTimeout(() => {
      if (target === 'flow' && !flowGenerated) {
        pulseFlowCard();
        return;
      }
      const f = document.querySelector('.ags-flow .ags-ctl');
      if (f && ['INPUT', 'TEXTAREA', 'SELECT'].includes(f.tagName)) f.focus({ preventScroll: true });
    }, 450);
  };

  
  const exportJson = () => {
    const { user_id, reseller_id, agent_id, draft_id, status: _status, ...rest } = buildSavePayload();
    const blob = new Blob([JSON.stringify(rest, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const slug = (agentName || 'agent').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'agent';
    a.href = url;
    a.download = `${slug}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Agent JSON downloaded');
  };

  const saveDraftNow = async () => {
    setPopOpen(false);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    dirtyRef.current = true;
    await persistDraft();
    toast.success('Draft saved');
  };

  const statusPill = status === 'published' ? 'Live' : missing.length ? 'Draft' : 'Ready';
  const studio = { readOnly, touched, touch, attempted, data, goTo: jumpTo, formState, flowGenerated };
  const note = NOTES[selectedId.startsWith('laya-') ? 'laya' : selectedId];
  const jumpList = useMemo(
    () => order.map((n) => ({ id: n.id, label: n.label })),
    [order],
  );

  return (
    <StudioContext.Provider value={studio}>
      <div className="ags" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0 }}>
        <div className="ags-scroll" ref={scrollRef}>
          <div className="ags-wrap">
            <Header
              agentName={agentName}
              setAgentName={setAgentName}
              readOnly={readOnly}
              typeLabel={agentTypeLabel}
              statusPill={statusPill}
              draftState={draftState}
              lastUpdatedLabel={lastUpdatedLabel}
              canImport={canImport}
              onImport={() => setImportOpen(true)}
              onExport={exportJson}
              onBack={handleBack}
              isSuperAdmin={isSuperAdmin}
              saving={saving}
              onPublish={handlePublish}
              onSaveDraft={saveDraftNow}
              popOpen={popOpen}
              setPopOpen={setPopOpen}
              items={items}
              left={hero.left}
              goTo={jumpTo}
            />
            <ViewBar show={readOnly && !isSuperAdmin} onEdit={() => setViewOnly(false)} />
            <Hero hero={hero} />

            <div className={c('cols')} ref={colsRef}>
              <BuilderTree
                nodes={nodes}
                selectedId={selectedId}
                onSelect={selectNode}
                expanded={expanded}
                onToggle={(id) => setExpanded((e) => ({ ...e, [id]: !e[id] }))}
                search={
                  <NavSearch
                    ref={searchRef}
                    jump={jumpList}
                    goTo={jumpTo}
                    canImport={canImport}
                    laya={laya}
                    onAddFast={() => {
                      setLaya((l) => ({ ...l, enabled: true }));
                      jumpTo('laya');
                    }}
                    onToggleFast={() => setLaya((l) => ({ ...l, enabled: !l.enabled }))}
                    onImport={() => setImportOpen(true)}
                    onExport={exportJson}
                  />
                }
              />

              <div className={c('flow', readOnly && 'ro')} aria-disabled={readOnly || undefined}>
                {note && !readOnly && <p className={c('optnote')}>Optional: {note}</p>}
                {renderDetail()}

               
                <div className={c('steps')}>
                  <button type="button" className={c('btn primary')} disabled={!prevNode} onClick={() => goTo(prevNode)}>
                    <ChevronLeft className="ags-i" />
                    <span>{prevNode ? prevNode.label : 'Previous'}</span>
                  </button>
                  {nextNode ? (
                    <button type="button" className={c('btn primary')} onClick={() => goTo(nextNode)}>
                      <span>{nextNode.label}</span>
                      <ChevronRight className="ags-i" />
                    </button>
                  ) : readOnly ? (
                    <span />
                  ) : missing.length > 0 ? (
                    <button
                      type="button"
                      className={c('btn fixbtn')}
                      onClick={() => (missing[0].id === 'flow' ? generateFlowFromFix() : jumpTo(missing[0].id))}
                    >
                      <span>{missing[0].id === 'flow' ? (missing.length > 1 ? `Generate flow chart (${missing.length} missing)` : 'Generate flow chart') : `Fix ${missing.length} missing`}</span>
                      <ChevronRight className="ags-i" />
                    </button>
                  ) : (
                    <button type="button" className={c('btn primary')} onClick={handlePublish} disabled={saving}>
                      {saving ? <Loader2 className="ags-i" style={{ animation: 'ags-spin 1s linear infinite' }} /> : <Save className="ags-i" />}
                      All set, publish
                    </button>
                  )}
                </div>
              </div>

              <Rail
                form={formState}
                data={data}
                before={
                  <FlowCard
                    controlRef={flowCardRef}
                    buildPayload={buildFlowPayload}
                    agentName={agentName}
                    instructions={instructions}
                    selectedKbId={selectedKbId}
                    flowGenerated={flowGenerated}
                    readOnly={readOnly}
                    canPublish={!isSuperAdmin && !readOnly}
                    saving={saving}
                    onPublish={handlePublish}
                    onGoTo={jumpTo}
                    onFlowGenerated={() => {
                      setFlowGenerated(true);
               
                      if (!conversationFlow?.trim() && defaultFlow) setConversationFlow(defaultFlow);
                    }}
                    initialFlow={loadedAgent?.flow_chart_data}
                  />
                }
                // Agent summary card hidden for now (uncomment to bring it back)
                // middle={<AgentSummary lines={summaryLines} missing={missing} />}
              />
            </div>
          </div>
        </div>

        <Dock items={items} goTo={jumpTo} onPublish={handlePublish} show={!readOnly && !isSuperAdmin} />
      </div>

      <ImportJsonDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={applyImport}
        options={{ agentType, user, normalizers: { normalizeType: normalizeVariableType, normalizeSource: normalizeVariableSource } }}
      />
    </StudioContext.Provider>
  );
};

const AgentBuilderV2Page = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initial = useRef({
    draftId: searchParams.get('draftId'),
    agentId: searchParams.get('id'),
    stateAgent: location.state?.agent || null,
  });
  const { draftId: initialDraftId, agentId: initialAgentId, stateAgent } = initial.current;
  const needsFetch = Boolean(initialDraftId || initialAgentId);

  const [ready, setReady] = useState(!needsFetch);
  const [loadedAgent, setLoadedAgent] = useState(needsFetch ? null : stateAgent);

  useEffect(() => {
    if (!needsFetch) return;
    let cancelled = false;
    const user = getUser();
    const request = initialDraftId ? getDraft(initialDraftId) : getAgent(initialAgentId, user?.id);
    request
      .then((fresh) => {
        if (!cancelled) setLoadedAgent(fresh || stateAgent);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadedAgent(stateAgent);
        if (!stateAgent) toast.error(typeof err === 'string' ? err : 'Failed to load agent');
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return (
      <FillPage>
        <PageLoader label="Loading agent…" />
      </FillPage>
    );
  }
  return <AgentBuilderEditor loadedAgent={loadedAgent} />;
};

export default AgentBuilderV2Page;
